import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogFooter,
  Input,
  Button,
} from "@material-tailwind/react";
import { toast } from "react-toastify";
import Paper from "@mui/material/Paper";
import { DataGrid } from "@mui/x-data-grid";

import { useUserContext } from "../../contexts/UserContextProvider";
import { usePaymentsContext } from "../../contexts/PaymentsContextProvider";
import { paymentConfig } from "../../config/paymentConfig.js";
import {
  phoneNumberSanitation,
  convertToCommaNumbers,
} from "../utils/inputSanitation.js";

const RechargeModule = () => {
  const {
    // rechargeViaPhonePe,
    getCoinsDetails,
    getCoinsRechargeTier,
    getFeatureCoinsDetails,
    rechargeViaRazorpay,
  } = usePaymentsContext();
  const { currentUserInfo } = useUserContext();

  const [featureCoinsDetails, setFeatureCoinsDetails] = useState([]);
  const [rechargeAmount, setRechargeAmount] = useState(
    paymentConfig.minRechargeAmtINR
  );
  const [gstAmount, setGstAmount] = useState(0);
  const [totalCoinsGained, setTotalCoinsGained] = useState(0);
  const [balanceCoins, setBalanceCoins] = useState(0);
  const [coinsRechargeTier, setCoinsRechargeTier] = useState([]);
  const [isOpenRechargeDialog, setIsOpenRechargeDialog] = useState(false);
  const [currentConvRate, setCurrentConvRate] = useState();
  const [currencySymbol, setCurrencySymbol] = useState("₹");

  const rechargeTierTableColumns = [
    {
      field: "min_recharge_amount",
      headerName: "Min. Recharge Amount",
      width: 200,
      sortable: true,
      valueGetter: (value, row) =>
        convertToCommaNumbers(row?.min_recharge_amount),
    },
    {
      field: "no_of_coins",
      headerName: "No. of Coins",
      width: 200,
      sortable: true,
      valueGetter: (value, row) =>
        convertToCommaNumbers(
          currentUserInfo?.rechargeCurrency?.toUpperCase() === "INR"
            ? row?.min_recharge_amount * 100
            : row?.min_recharge_amount * 100 * 50
        ),
    },
    {
      field: "no_of_conv",
      headerName: "No. of Conversations",
      width: 200,
      sortable: true,
      valueGetter: (value, row) =>
        convertToCommaNumbers(
          Math.floor(
            currentUserInfo?.rechargeCurrency?.toUpperCase() === "INR"
              ? (row?.min_recharge_amount * 100) / row?.conv_rate
              : (row?.min_recharge_amount * 100 * 50) / row?.conv_rate
          )
        ),
    },
    {
      field: "outgoing_coins_ded",
      headerName: "Outgoing Coins Deduction",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.description,
    },
    {
      field: "validity_in_days",
      headerName: "Coins Validity (Days)",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.validity_in_days,
    },
  ];

  const featureCoinsTableColumns = [
    {
      field: "feature_name",
      headerName: "Feature Name",
      width: 500,
      sortable: true,
      valueGetter: (value, row) => row?.feature_name,
    },
    {
      field: "feature_remarks",
      headerName: "Coins Cost",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.feature_remarks,
    },
  ];

  let res;
  const rechargeOnClick = async () => {
    // if (currentUserInfo?.rechargeCurrency?.toUpperCase() === "INR") {
    //   res = await rechargeViaPhonePe(
    //     parseFloat(rechargeAmount) + parseFloat(gstAmount),
    //     "recharge",
    //     parseFloat(gstAmount)
    //   );
    // } else {
    //   res = await rechargeViaRazorpay(
    //     parseFloat(rechargeAmount) + parseFloat(gstAmount),
    //     "recharge",
    //     parseFloat(gstAmount)
    //   );
    // }

    // Default payment routed to razorpay
    res = await rechargeViaRazorpay(
      parseFloat(rechargeAmount) + parseFloat(gstAmount),
      "recharge",
      parseFloat(gstAmount)
    );

    if (res?.Error) {
      toast.error(res.Error);
    } else if (res?.paymentLink) {
      window.open(res.paymentLink, "_blank");
      setRechargeAmount(0);
    } else {
      toast.error(
        "Failed to process the payment request. Please contact WaBM support team."
      );
    }
  };

  const calculateRechargeAmount = async () => {
    if (
      rechargeAmount < parseFloat(paymentConfig.minRechargeAmtINR) &&
      currentUserInfo?.isInternationalMessageAllowed === 0
    ) {
      toast.error(
        `Minimum recharge amount is ${parseFloat(
          paymentConfig.minRechargeAmtINR
        )}`
      );
    } else if (
      rechargeAmount < parseFloat(paymentConfig.minRechargeAmtUSD) &&
      currentUserInfo?.isInternationalMessageAllowed === 1
    ) {
      toast.error(
        `Minimum recharge amount is ${parseFloat(
          paymentConfig.minRechargeAmtUSD
        )}`
      );
    } else {
      if (currentUserInfo?.rechargeCurrency?.toUpperCase() === "INR") {
        const gstAmount = (
          (rechargeAmount * parseFloat(paymentConfig.rechargeGST)) /
          100
        ).toFixed(2);
        setGstAmount(gstAmount);
      } else {
        setGstAmount(0);
      }

      const filterData = coinsRechargeTier.find(
        (x) =>
          parseFloat(x.min_recharge_amount) <= parseFloat(rechargeAmount) &&
          parseFloat(x.max_recharge_amount) >= parseFloat(rechargeAmount)
      );

      let totalCoinsGained = 0;

      if (filterData?.currency?.toUpperCase() === "INR") {
        totalCoinsGained = (rechargeAmount * 100).toFixed(0);
        setCurrencySymbol("₹");
      } else {
        setCurrencySymbol(currentUserInfo?.currencySymbol);
        totalCoinsGained = (
          rechargeAmount *
          100 *
          parseFloat(filterData?.international_coins_conv_rate)
        ).toFixed(0);
      }
      setTotalCoinsGained(totalCoinsGained);
      setCurrentConvRate(filterData);
      setIsOpenRechargeDialog(true);
    }
  };

  const fetchCoinsDetails = async () => {
    const coinsDetails = await getCoinsDetails();

    if (coinsDetails && coinsDetails.result) {
      setBalanceCoins(coinsDetails.result?.[0]?.balanceCoins);
    }
  };

  const fetchCoinsRechargeTier = async () => {
    const coinsRechargeTier = await getCoinsRechargeTier();
    if (coinsRechargeTier && coinsRechargeTier.result) {
      setCoinsRechargeTier(coinsRechargeTier.result);
    }
  };

  useEffect(() => {
    fetchCoinsDetails();
    fetchCoinsRechargeTier();

    const featureCoins = async () => {
      const result = await getFeatureCoinsDetails();
      setFeatureCoinsDetails(result?.result);
    };

    featureCoins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <main className="w-full mt-5 mb-10 lg:my-1 space-y-5">
        <div className="w-full space-y-2">
          <h1 className="text-2xl">Manage Recharge</h1>
          <h3>These are details about all your current and past recharge.</h3>
        </div>
        <div className="wbm-card-2 mt-8 space-y-8">
          <div className="wbm-card-3 space-y-2">
            <div className="flex gap-3">
              <h3 className="font-Montserrat text-xl">Balance Coins</h3>
              <span>|</span>
              <p
                className={`${
                  balanceCoins > paymentConfig.minCoinsForWarning
                    ? "text-green-600"
                    : "text-red-600"
                } font-Montserrat text-xl`}
              >
                {balanceCoins ? convertToCommaNumbers(balanceCoins) : 0} coins
              </p>
            </div>
            {balanceCoins < paymentConfig.minCoinsForWarning && (
              <p className="text-sm text-red-500">
                Low coins in your wallet. Recharge now to keep messaging.
              </p>
            )}
          </div>

          <div className="lg:w-1/3 space-y-3">
            <h3 className="font-Montserrat text-lg">Recharge Now</h3>
            <Input
              variant="outlined"
              label="Enter your Recharge Amount"
              color="blue"
              required
              placeholder={`Enter your prferred recharge amount`}
              value={rechargeAmount}
              onChange={(e) =>
                setRechargeAmount(phoneNumberSanitation(e.target.value))
              }
              className="my-card-style-1 bg-white"
            />
            <Button
              type="button"
              variant="gradient"
              color="blue"
              onClick={calculateRechargeAmount}
            >
              Calculate Amount
            </Button>
          </div>

          <div className="mt-5 space-y-3 rounded-md">
            <h3 className="font-Montserrat text-lg">Recharge Tier</h3>
            <Paper style={{ position: "relative", zIndex: 1, height: 550 }}>
              <DataGrid
                getRowId={(row) => row?.co_re_tier_id_pk}
                rows={coinsRechargeTier}
                columns={rechargeTierTableColumns}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "background.paper",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    marginTop: "0px",
                  },
                }}
                disableColumnMenu={false}
                disableRowSelectionOnClick={true}
                hideFooterPagination
              />
            </Paper>
          </div>
          <div className="mt-5 space-y-3 rounded-md">
            <h3 className="font-Montserrat text-lg">
              App Feature Coins Deduction
            </h3>
            <Paper style={{ position: "relative", zIndex: 1, height: 550 }}>
              <DataGrid
                getRowId={(row) => row?.id}
                rows={featureCoinsDetails}
                columns={featureCoinsTableColumns}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "background.paper",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    marginTop: "0px",
                  },
                }}
                disableColumnMenu={false}
                disableRowSelectionOnClick={true}
                hideFooterPagination
              />
            </Paper>
          </div>
        </div>
      </main>
      <Dialog
        open={isOpenRechargeDialog}
        handler={() => setIsOpenRechargeDialog(!isOpenRechargeDialog)}
      >
        <DialogHeader>
          <h2 className="wabm-font-style-2">Recharge Details</h2>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col justify-start gap-2">
            <span className="grid grid-cols-2 gap-3">
              <h3 className="border-r-[0.5px] border-slate-400">
                Recharge Amount
              </h3>
              <p>
                {currencySymbol}
                {rechargeAmount}
              </p>
            </span>
            {gstAmount > 0 && (
              <span className="grid grid-cols-2 gap-3">
                <h3 className="border-r-[0.5px] border-slate-400">
                  GST Amount
                </h3>
                <p>
                  {currencySymbol}
                  {gstAmount}{" "}
                  <span className="ml-2 text-blue-700">
                    ({paymentConfig.rechargeGST}%)
                  </span>
                </p>
              </span>
            )}
            <span className="grid grid-cols-2 gap-3">
              <h3 className="border-r-[0.5px] border-slate-400">
                Total Recharge Amount
              </h3>
              <p>
                {currencySymbol}
                {parseFloat(rechargeAmount) + parseFloat(gstAmount)}
              </p>
            </span>
            <span className="grid grid-cols-2 gap-3">
              <h3 className="border-r-[0.5px] border-slate-400">
                Total coins gained
              </h3>
              <p>{totalCoinsGained} coins</p>
            </span>
            <span className="grid grid-cols-2 gap-3">
              <h3 className="border-r-[0.5px] border-slate-400">
                Current Coins Deduction Tier
              </h3>
              <span className="text-blue-700">
                -{currentConvRate?.description}
              </span>
            </span>
            <span className="mt-2 text-orange-600 text-sm">
              💡 Tip : Recharge higher amount to get less coin deduction per
              conversation
            </span>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="gradient"
            color="green"
            onClick={rechargeOnClick}
            className="mx-auto"
          >
            Recharge
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};
export default RechargeModule;
